import React from 'react'
import { api } from '../lib/api'
import { DialogBackdrop, DialogPanel } from './DialogLayout'

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * 이미지 자료실 ID 배열을 순서대로 미리보기·다운로드.
 * @param {{ imageIds: number[], title?: string, onClose: () => void }} props
 */
export default function LibraryMappedImagesModal({ imageIds, title, onClose }) {
  const ids = React.useMemo(
    () => (Array.isArray(imageIds) ? imageIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n >= 1) : []),
    [imageIds],
  )
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [metas, setMetas] = React.useState([])
  const [previewUrls, setPreviewUrls] = React.useState({})
  const [downloadingId, setDownloadingId] = React.useState(null)
  const [downloadErr, setDownloadErr] = React.useState(null)

  React.useEffect(() => {
    let cancelled = false
    const urls = []

    async function run() {
      setLoading(true)
      setError(null)
      setMetas([])
      setPreviewUrls({})
      if (ids.length === 0) {
        setLoading(false)
        return
      }
      try {
        const metaList = await Promise.all(
          ids.map(async (id) => {
            const res = await api.get(`/gemini/images/${id}`)
            const d = res?.data?.data ?? res?.data
            return d && typeof d === 'object' ? { ...d, _id: id } : null
          }),
        )
        const ok = metaList.filter(Boolean)
        if (cancelled) return
        setMetas(ok)
        const next = {}
        for (const m of ok) {
          const fid = m.fileId
          if (!fid) continue
          try {
            const res = await api.get(`/files/${fid}/preview`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
            const u = URL.createObjectURL(blob)
            urls.push(u)
            next[m.id ?? m._id] = u
          } catch {
            // ignore per-image
          }
        }
        if (!cancelled) setPreviewUrls(next)
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e.message || '이미지를 불러오지 못했습니다.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
      urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u)
        } catch {
          /* ignore */
        }
      })
    }
  }, [ids])

  const downloadOne = async (meta) => {
    const fid = meta?.fileId
    const id = meta?.id
    if (!fid || downloadingId != null) return
    setDownloadingId(id)
    setDownloadErr(null)
    try {
      const res = await api.get(`/files/${fid}/download`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
      const cd = res.headers?.['content-disposition'] || ''
      const m = /filename="?([^";]+)"?/i.exec(cd)
      const fallback = meta.imageName || `library_image_${id}.png`
      const filename = m ? m[1] : fallback
      downloadBlob(blob, filename)
    } catch {
      setDownloadErr('다운로드에 실패했습니다.')
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <DialogBackdrop className="z-[160]" onClick={onClose}>
      <DialogPanel
        className="max-w-3xl sm:max-h-[min(92dvh,40rem)]"
        aria-label={title || '\uC5F0\uACB0\uB41C \uC774\uBBF8\uC9C0'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0">
          <div className="font-semibold text-slate-900 truncate">{title || '연결된 이미지'}</div>
          <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 shrink-0" onClick={onClose}>
            닫기
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {downloadErr && <div className="text-sm text-red-700">{downloadErr}</div>}
          {loading && <div className="text-sm text-slate-600">불러오는 중…</div>}
          {error && <div className="text-sm text-red-700">{error}</div>}
          {!loading && !error && metas.length === 0 && <div className="text-sm text-slate-500">표시할 이미지가 없습니다.</div>}
          {!loading &&
            metas.map((meta, idx) => {
              const pid = meta.id
              const src = previewUrls[pid]
              return (
                <div key={pid || idx} className="rounded border border-slate-200 p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                    <span>
                      순서 {idx + 1} · ID <span className="font-mono text-slate-800">{pid}</span>
                      {meta.category ? ` · ${meta.category}` : ''}
                    </span>
                    <button
                      type="button"
                      className="rounded border border-brand-navy text-brand-navy px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
                      disabled={!meta.fileId || downloadingId != null}
                      onClick={() => void downloadOne(meta)}
                    >
                      {downloadingId === pid ? '다운로드 중…' : '다운로드'}
                    </button>
                  </div>
                  {meta.caption ? (
                    <div className="text-xs text-slate-700 whitespace-pre-wrap max-h-24 overflow-y-auto rounded bg-slate-50 px-2 py-1.5">
                      {meta.caption}
                    </div>
                  ) : null}
                  <div className="flex justify-center bg-slate-50 rounded border border-slate-100 p-2">
                    {src ? (
                      <img
                        src={src}
                        alt=""
                        className="max-h-[min(50vh,22rem)] max-w-full w-auto h-auto object-contain"
                      />
                    ) : (
                      <div className="text-xs text-slate-500 py-8">미리보기를 불러오지 못했습니다.</div>
                    )}
                  </div>
                </div>
              )
            })}
        </div>
      </DialogPanel>
    </DialogBackdrop>
  )
}
